import React, { useState, useEffect, useRef, createContext, useContext, useCallback, Suspense, lazy } from 'react';
import { supabase } from './lib/supabase';
import { clearSessionEverywhere, EXPIRED_FLAG } from './lib/sessionTimeout';
import { markOffline } from './lib/dialer';
import { recordOwnTimeZone } from './lib/tz';
import SessionTimeoutGuard from './components/SessionTimeoutGuard';
import ChangePasswordScreen from './components/ChangePasswordScreen';
import LoginPage from './pages/LoginPage';
// Each role's workspace is its own download, so people only load the one they use.
// After a new version is deployed, a window still running the old version
// asks for files that no longer exist. Reload once to pick up the new version
// instead of showing a blank screen (at most once a minute, so a real outage
// can't cause a reload loop).
const RELOAD_KEY = 'mm-chunk-reload';
function loadOrReload(load) {
  return () => load().catch(err => {
    let last = 0;
    try { last = +window.sessionStorage.getItem(RELOAD_KEY) || 0; } catch (e) { /* storage blocked */ }
    if (Date.now() - last > 60000) {
      try { window.sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch (e) { /* storage blocked */ }
      window.location.reload();
      return new Promise(() => {}); // keep the loading screen up while the page reloads
    }
    throw err;
  });
}
const LOADERS = {
  director: loadOrReload(() => import('./pages/DirectorApp')),
  manager: loadOrReload(() => import('./pages/DirectorApp')),
  dba: loadOrReload(() => import('./pages/DBAApp')),
  executive: loadOrReload(() => import('./pages/ExecutiveApp')),
  operations: loadOrReload(() => import('./pages/OperationsApp')),
};
const DirectorApp = lazy(LOADERS.director);
const DBAApp = lazy(LOADERS.dba);
const ExecutiveApp = lazy(LOADERS.executive);
const OperationsApp = lazy(LOADERS.operations);
// Start downloading the workspace this browser used last time straight away,
// while sign-in and the profile are still loading.
const ROLE_KEY = 'mm-last-role';
try { const r = window.localStorage.getItem(ROLE_KEY); if (LOADERS[r]) LOADERS[r]().catch(() => {}); } catch (e) { /* storage blocked */ }
import './App.css';

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // 'ok' | 'missing' (signed in, but no profile row) | 'error' (could not load)
  const [profileState, setProfileState] = useState('ok');
  const profileRef = useRef(null);
  profileRef.current = profile;

  // Only the newest request may set the profile, so a slow earlier response
  // can't overwrite a newer one.
  const fetchSeq = useRef(0);
  const inFlight = useRef(null);      // uid being fetched right now
  const refreshedFor = useRef(null);  // uid we already refreshed the token for
  const fetchProfile = useCallback(async (uid) => {
    const seq = ++fetchSeq.current;
    inFlight.current = uid;
    for (let attempt = 0; ; attempt++) {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', uid).maybeSingle();
      if (seq !== fetchSeq.current) return;
      if (error) {
        // A transient failure must not throw a signed-in agent back to the login
        // screen (which also unmounts the dialer mid-call): keep the last good
        // profile and only report a problem when there is none to fall back on.
        console.warn('Could not load the profile:', error.message);
        if (!profileRef.current || profileRef.current.id !== uid) setProfileState('error');
      } else if (data) {
        try { window.localStorage.setItem(ROLE_KEY, data.role); } catch (e) { /* storage blocked */ }
        if (LOADERS[data.role]) LOADERS[data.role]().catch(() => {});
        setProfile(data);
        setProfileState('ok');
        recordOwnTimeZone(data);
      } else if (attempt < 3) {
        // No row yet usually means the sign-in token hadn't reached the request
        // (or a new account's profile is still being created). Refresh and try
        // again before saying the profile is missing.
        if (attempt === 0 && refreshedFor.current !== uid) {
          refreshedFor.current = uid;
          await supabase.auth.refreshSession().catch(() => {});
        }
        await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
        if (seq !== fetchSeq.current) return;
        continue;
      } else {
        setProfile(null);
        setProfileState('missing');
      }
      break;
    }
    if (seq === fetchSeq.current) inFlight.current = null;
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else setLoading(false);
    });
    // Only events that can change who is signed in (or their details) refetch
    // the profile. TOKEN_REFRESHED fires every hour and on every tab focus, and
    // refetching on it is what turned a network blip into a sign-out.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (!session) { fetchSeq.current++; inFlight.current = null; refreshedFor.current = null; setProfile(null); setProfileState('ok'); setLoading(false); return; }
      const switched = profileRef.current && profileRef.current.id !== session.user.id;
      // Already fetching this user's profile (the retry's own token refresh
      // fires SIGNED_IN / TOKEN_REFRESHED): let that request finish.
      if (inFlight.current === session.user.id && !switched) return;
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION' || switched || (!profileRef.current && event !== 'TOKEN_REFRESHED')) {
        fetchProfile(session.user.id);
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  // A server-set must_change_password (app_metadata) cannot be cleared from the
  // browser. Once ChangePasswordScreen has saved a new password (it clears the
  // user_metadata copy), ask the database to clear the server flag; it only
  // does so if the password really changed. Then refresh the token so the new
  // app_metadata reaches this session.
  const clearingRef = useRef(null);
  useEffect(() => {
    const u = session?.user;
    if (!u?.app_metadata?.must_change_password) return;
    if (u.user_metadata?.must_change_password !== false) return;
    const key = u.id + ':' + (u.updated_at || '');
    if (clearingRef.current === key) return;
    clearingRef.current = key;
    (async () => {
      const { error } = await supabase.rpc('clear_must_change_password');
      if (error) { console.warn('clear_must_change_password:', error.message); return; }
      await supabase.auth.refreshSession();
    })();
  }, [session]);

  // Sign out through the existing Supabase flow, additionally clearing tokens,
  // cookies, cached session data and stored credentials. When triggered by the
  // inactivity timeout, a shared flag tells the login screen to explain why.
  const signOut = useCallback(async (opts) => {
    const expired = !!(opts && opts.expired === true);
    if (expired) {
      try { window.localStorage.setItem(EXPIRED_FLAG, '1'); } catch (e) { /* ignore */ }
    }
    // Tell the dialer presence we're leaving while still signed in.
    await Promise.race([markOffline(), new Promise(r => setTimeout(r, 1500))]);
    await clearSessionEverywhere();
    setSession(null); setProfile(null);
  }, []);

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-gem"><img src="/brand/logo-mark-sm.png" alt="Momentum Miles" /></div>
      <div className="loading-text">Loading Momentum Miles...</div>
    </div>
  );

  if (!session) return <LoginPage />;

  // Signed in, profile still on its way: keep the loading screen up rather than
  // flashing "No profile" for a moment.
  if (!profile && profileState === 'ok') return (
    <div className="loading-screen">
      <div className="loading-gem"><img src="/brand/logo-mark-sm.png" alt="Momentum Miles" /></div>
      <div className="loading-text">Loading Momentum Miles...</div>
    </div>
  );

  // Signed in, but there is no usable profile. Saying so (with a way out)
  // replaces the old silent bounce back to the login screen.
  if (!profile) return (
    <div className="loading-screen">
      <div className="loading-gem"><img src="/brand/logo-mark-sm.png" alt="Momentum Miles" /></div>
      <div className="loading-text">
        {profileState === 'error'
          ? <>Your profile could not be loaded. Check your connection and try again.</>
          : <>No profile for this account — contact your DBA.<br />{session.user?.email}</>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {profileState === 'error' && (
          <button className="btn btn-primary" onClick={() => fetchProfile(session.user.id)}>Try again</button>
        )}
        <button className="btn btn-ghost" onClick={() => signOut({ expired: false })}>Sign out</button>
      </div>
    </div>
  );

  // A DBA reset this account to a temporary password: choose a new one first.
  // app_metadata can only be set server-side (admin-users); user_metadata is
  // editable by the user, so it is honoured but is not the only signal.
  if (session.user?.app_metadata?.must_change_password || session.user?.user_metadata?.must_change_password)
    return <ChangePasswordScreen email={session.user.email} onSignOut={() => signOut({ expired: false })} />;

  return (
    <AuthContext.Provider value={{ session, profile, signOut }}>
      <SessionTimeoutGuard signOut={signOut} />
      <Suspense fallback={<div className="loading-screen"><div className="loading-gem"><img src="/brand/logo-mark-sm.png" alt="Momentum Miles" /></div><div className="loading-text">Loading Momentum Miles...</div></div>}>
      {(profile.role === 'director' || profile.role === 'manager') && <DirectorApp />}
      {profile.role === 'dba' && <DBAApp />}
      {profile.role === 'executive' && <ExecutiveApp />}
      {profile.role === 'operations' && <OperationsApp />}
      {!['director','manager','dba','executive','operations'].includes(profile.role) && (
        <div className="loading-screen">
          <div className="loading-gem"><img src="/brand/logo-mark-sm.png" alt="Momentum Miles" /></div>
          <div className="loading-text">
            No workspace is set up for the {profile.role} role yet.
            <br />Ask your director to check your account.
          </div>
          <button className="btn btn-ghost" style={{ marginTop: 16 }}
            onClick={() => signOut({ expired: false })}>Sign out</button>
        </div>
      )}
      </Suspense>
    </AuthContext.Provider>
  );
}
